import React from "react";
import useReveal from "../hooks/useReveal";
import {
  ClipboardCheck,
  Wrench,
  ShieldCheck,
  Disc,
  Gauge,
  Snowflake,
  ArrowUpRight,
  Droplets,
  FileSearch,
  Car,
  Sparkles,
} from "lucide-react";

const services = [
  {
    title: "Inspektion",
    desc: "Gründliche Überprüfung aller sicherheitsrelevanten Komponenten nach Herstellervorgaben.",
    icon: ClipboardCheck,
  },
  {
    title: "Reparaturen",
    desc: "Alle Fabrikate, alle Modelle. Speziell Renault: über 40 Jahre Erfahrung in unserer Werkstatt.",
    icon: Wrench,
  },
  {
    title: "TÜV / HU",
    desc: "Hauptuntersuchung direkt bei uns. Vorprüfung inklusive, damit Sie beim ersten Mal bestehen.",
    icon: ShieldCheck,
  },
  {
    title: "Reifenservice",
    desc: "Montage, Auswuchten, Achsvermessung und Einlagerung Ihrer Sommer- und Winterreifen.",
    icon: Disc,
  },
  {
    title: "Diagnose",
    desc: "Fehlerauslese über moderne OBD-Systeme. Wir finden die Ursache, nicht nur das Symptom.",
    icon: Gauge,
  },
  {
    title: "Klimaservice",
    desc: "Desinfektion, Dichtigkeitsprüfung und Nachfüllen — für kühle Sommer und klare Scheiben.",
    icon: Snowflake,
  },
  {
    title: "Automatik-Getriebespülung",
    desc: "Professionelle Spülung nach Tim-Eckhard-Methode. Schützt das Getriebe und verlängert die Lebensdauer.",
    icon: Droplets,
    link: "https://www.automatikoelwechselsystem.de",
    linkLabel: "Mehr erfahren",
  },
  {
    title: "KFZ-Gutachten",
    desc: "Schaden- und Wertgutachten für Privat- und Geschäftskunden — schnell, neutral, beweissicher.",
    icon: FileSearch,
  },
  {
    title: "Oldtimer-Reparaturen",
    desc: "Behutsame Reparaturen und Wartung historischer Fahrzeuge — mit Respekt für jedes Detail.",
    icon: Car,
  },
  {
    title: "Auto-Aufbereitung",
    desc: "Innen- und Außenpflege, Politur, Lederpflege — Ihr Fahrzeug erstrahlt wieder wie am ersten Tag.",
    icon: Sparkles,
  },
];

export default function Services() {
  const [ref, visible] = useReveal();
  return (
    <section
      id="leistungen"
      ref={ref}
      data-testid="services-section"
      className={`relative bg-[#09090b] border-t border-zinc-900 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-24 md:py-32">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-[11px] tracking-[0.4em] uppercase gold-text mb-4">
              — Leistungen
            </div>
            <h2
              data-testid="services-heading"
              className="font-display text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-3xl"
            >
              Alles fürs Auto.
              <br />
              <span className="text-zinc-500">Unter einem Dach.</span>
            </h2>
            <p className="mt-5 text-sm md:text-base text-zinc-400 max-w-xl">
              Reparaturen für alle Fabrikate. Speziell{" "}
              <span className="gold-text font-medium">Renault</span> —
              hier bringen wir <strong className="text-white">40 Jahre Erfahrung</strong>{" "}
              direkt aus der Werkstatt mit.
            </p>
          </div>
          <a
            href="#termin"
            data-testid="services-cta"
            className="hidden md:inline-flex self-end items-center gap-2 text-sm uppercase tracking-[0.25em] text-zinc-300 hover:gold-text border-b border-zinc-700 hover:border-[#E0B33D] pb-1 transition-colors"
          >
            Termin anfragen <ArrowUpRight size={16} />
          </a>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isExternal = !!s.link;
            const href = s.link || "#termin";
            return (
              <a
                key={s.title}
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noreferrer" : undefined}
                data-testid={`service-card-${s.title.toLowerCase().replace(/[\/\s]/g, "-")}`}
                className="group relative bg-[#0f0f12] p-7 md:p-8 min-h-[220px] flex flex-col justify-between hover:bg-[#18181b] transition-colors cursor-pointer"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="w-10 h-10 grid place-items-center gold-bg text-black">
                      <Icon size={20} strokeWidth={2.25} />
                    </span>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-600">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display mt-6 text-xl md:text-2xl uppercase tracking-tight leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                </div>
                {isExternal ? (
                  <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase gold-text border-b border-[#E0B33D]/40 group-hover:border-[#FFD75E] pb-0.5 self-start transition-colors">
                    {s.linkLabel || "Mehr erfahren"} <ArrowUpRight size={12} />
                  </div>
                ) : (
                  <div className="mt-6 flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-zinc-500 group-hover:gold-text transition-colors">
                    Anfrage stellen <ArrowUpRight size={12} />
                  </div>
                )}
              </a>
            );
          })}

          {/* Filler card with note */}
          <div className="gold-bg text-black p-7 md:p-8 min-h-[220px] flex flex-col justify-between">
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase">
                Unsicher?
              </div>
              <h3 className="font-display mt-4 text-2xl md:text-3xl uppercase tracking-tight leading-[0.95]">
                Rufen Sie uns
                <br />
                einfach an.
              </h3>
            </div>
            <a
              href="tel:02131544154"
              data-testid="services-call-cta"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.2em] uppercase border-b border-black/50 hover:border-black pb-1 self-start"
            >
              02131 544154
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
